import type { Block } from 'blockly/core';

import { arduinoGenerator, blockTraceComment, Order } from './arduino-generator.js';

const BUTTON_DEBOUNCE_MS = 20;

function statementPrefix(block: Block, code: string): string {
  return `${blockTraceComment(block)}\n${code}`;
}

function buttonPin(block: Block): string {
  return String(block.getFieldValue('BUTTON_PIN') ?? '2');
}

function pinIdentifier(pin: string): string {
  return pin.replace(/[^a-zA-Z0-9_]/g, '_');
}

function ensureButtonPinSetup(pin: string): void {
  arduinoGenerator.addSetupOnce(
    `beginner-button-${pin}`,
    `pinMode(${pin}, INPUT_PULLUP);`,
  );
}

function isPressedExpr(pin: string): string {
  // INPUT_PULLUP: pressed connects to GND → LOW
  return `(digitalRead(${pin}) == LOW)`;
}

function isReleasedExpr(pin: string): string {
  return `(digitalRead(${pin}) == HIGH)`;
}

export function registerBeginnerSwitchBlockGenerators(): void {
  arduinoGenerator.forBlock['beginner_switch_when_pressed'] = (block: Block) => {
    const pin = buttonPin(block);
    ensureButtonPinSetup(pin);
    const branch = arduinoGenerator.statementToCode(block, 'DO');
    const lastVar = `skyroverBtnLast_${pinIdentifier(pin)}`;
    arduinoGenerator.addGlobal(`bool ${lastVar} = HIGH;`);

    return statementPrefix(
      block,
      [
        `{`,
        `  bool skyroverBtnNow = digitalRead(${pin});`,
        `  if (${lastVar} == HIGH && skyroverBtnNow == LOW) {`,
        `    delay(${BUTTON_DEBOUNCE_MS});`,
        `    if (digitalRead(${pin}) == LOW) {`,
        branch || '',
        `    }`,
        `  }`,
        `  ${lastVar} = skyroverBtnNow;`,
        `}`,
        '',
      ].join('\n'),
    );
  };

  arduinoGenerator.forBlock['beginner_switch_when_released'] = (block: Block) => {
    const pin = buttonPin(block);
    ensureButtonPinSetup(pin);
    const branch = arduinoGenerator.statementToCode(block, 'DO');
    const lastVar = `skyroverBtnLast_${pinIdentifier(pin)}`;
    arduinoGenerator.addGlobal(`bool ${lastVar} = HIGH;`);

    return statementPrefix(
      block,
      [
        `{`,
        `  bool skyroverBtnNow = digitalRead(${pin});`,
        `  if (${lastVar} == LOW && skyroverBtnNow == HIGH) {`,
        `    delay(${BUTTON_DEBOUNCE_MS});`,
        `    if (digitalRead(${pin}) == HIGH) {`,
        branch || '',
        `    }`,
        `  }`,
        `  ${lastVar} = skyroverBtnNow;`,
        `}`,
        '',
      ].join('\n'),
    );
  };

  arduinoGenerator.forBlock['beginner_switch_is_pressed'] = (block: Block) => {
    const pin = buttonPin(block);
    ensureButtonPinSetup(pin);
    return [isPressedExpr(pin), Order.RELATIONAL];
  };

  arduinoGenerator.forBlock['beginner_switch_is_released'] = (block: Block) => {
    const pin = buttonPin(block);
    ensureButtonPinSetup(pin);
    return [isReleasedExpr(pin), Order.RELATIONAL];
  };

  arduinoGenerator.forBlock['beginner_switch_wait_pressed'] = (block: Block) => {
    const pin = buttonPin(block);
    ensureButtonPinSetup(pin);
    return statementPrefix(
      block,
      [
        `while (digitalRead(${pin}) == HIGH) {`,
        '  delay(5);',
        '}',
        `delay(${BUTTON_DEBOUNCE_MS});`,
        '',
      ].join('\n'),
    );
  };

  arduinoGenerator.forBlock['beginner_switch_wait_released'] = (block: Block) => {
    const pin = buttonPin(block);
    ensureButtonPinSetup(pin);
    return statementPrefix(
      block,
      [
        `while (digitalRead(${pin}) == LOW) {`,
        '  delay(5);',
        '}',
        `delay(${BUTTON_DEBOUNCE_MS});`,
        '',
      ].join('\n'),
    );
  };
}

registerBeginnerSwitchBlockGenerators();
